import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Skeleton,
  SxProps,
  ToggleButton,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useUiState } from '@utils/UiStateHook';
import DisabledCardState from '@components//Cards/DisabledCardState';
import { ToDate } from '@utils/DateUtils';
import { DateTime } from 'luxon';
import { GoalStatus } from '@app/enums/GoalStatus';
import { Circle, CircleOutlined } from '@mui/icons-material';
import { ConfirmGoalCompletionDialog, GoalWithStatus, UpdateGoalsDialog } from '@components';
import { useIsFirstWeek } from '@app/hooks/useIsFirstWeek';
import { ExerciseHistory } from '@components/modals';
import { useJailCardModalController } from '@components/GoalTracking/useJailCardModalController';
import { useConfirmGoalCompletionDialogController } from '@components/GoalTracking/useConfirmGoalCompletionDialogController';
import { JailCardStatusDialog } from '@components/GoalTracking/jail-card-status-dialog';
import { useUpdateGoalsDialogController } from '@components/GoalTracking/useUpdateGoalsDialogController';
import { useExerciseHistoryController } from '@components/GoalTracking/useExerciseHistoryController';
import { GoalSummaryView } from '@views';
import { useSurveyDrawerControllerContext } from '@components/SurveyDrawer/SurveyDrawerControllerProvider';
import { useGoalTrackerCardController } from '@components/GoalTracking/useGoalTrackerCardController';
import { InfoBubble } from '../InfoBubble';

interface GoalTrackerCardProps {
  groupStartDate: string | DateTime;
  cohortUserId: number;
  groupId: number;
  weekNumberIndexZero: number;
  reloadDashboardView?: () => void; // Optional prop to reload the dashboard view
}

export type GoalCompletionStatus = {
  [goalId: number]: GoalStatus[];
};

export const GoalTrackerCard2: React.FC<GoalTrackerCardProps> = ({
  groupStartDate,
  cohortUserId,
  groupId,
  weekNumberIndexZero,
  reloadDashboardView,
}) => {
  const uiState = useUiState(false);
  const styles = {
    card: {
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid',
      borderColor: uiState.borderColor,
      borderRadius: 4,
    } as SxProps,
  };

  const { view, isLoading, refetch, enabled, disabledMessage } = useGoalTrackerCardController(
    cohortUserId,
    groupStartDate
  );
  const { refresh: reloadSurveyDrawer } = useSurveyDrawerControllerContext();
  const { isFirstWeek } = useIsFirstWeek(ToDate(groupStartDate));
  const jailCardController = useJailCardModalController(view);
  const confirmGoalDialogController = useConfirmGoalCompletionDialogController(cohortUserId, refetch);
  const updateGoalsDialogController = useUpdateGoalsDialogController(groupId, refetch, ToDate(groupStartDate));
  const exerciseHistoryController = useExerciseHistoryController(cohortUserId);
  const { isDrawerOpen } = useSurveyDrawerControllerContext();

  const [newGoalEnabled, setNewGoalEnabled] = React.useState<boolean>(false);
  const [goals, setGoals] = React.useState<{
    goals: GoalSummaryView[];
    progress: GoalCompletionStatus;
  }>({
    goals: [],
    progress: {},
  });

  useEffect(() => {
    if (!isLoading && view) {
      setGoals(view.goalsWithProgress);
    }
  }, [isLoading, view]);

  useEffect(() => {
    // If this is not week 1
    // And goals exists
    // And the user has no active goals
    // Then enable the new goal button
    if (view) {
      setNewGoalEnabled(
        !isFirstWeek && (view.goalsWithProgress?.goals || []) && (view.goalsWithProgress?.goals?.length || 0) === 0
      );
    }
  }, [isFirstWeek, view]);

  return (
    <>
      <Card variant={'elevation'} sx={styles.card}>
        <CardContent>
          <div
            className="title-with-info"
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 6, // space between title and info icon
              flexWrap: 'nowrap',
            }}
          >
            <Typography variant="h5" component="div" color={enabled ? 'primary' : 'textDisabled'}>
              My Exercise Goal
            </Typography>
            <InfoBubble title="My Exercise Goal">
              Each time you complete an exercise activity, click one of the bubbles to record progress toward your goal.
              If you need to adjust your goal due to injury or illness, DM Emily in the chat spot. You have a chance to
              update your goal at the end of week 4 and week 8. If you want to tweak your goal or have any questions,
              just DM your coach.
            </InfoBubble>
          </div>
          {!enabled && (
            <DisabledCardState disabledMessage={disabledMessage} src="/assets/svg/calendar.svg" alt="Get stoked!" />
          )}
          {isLoading && (
            <ul>
              <li>
                <Typography color={uiState.color} variant={'h6'} sx={{ fontWeight: 300 }}>
                  <Skeleton width={'80%'}></Skeleton>
                </Typography>
              </li>
            </ul>
          )}
          {!isLoading && (
            <Grid container display={'flex'} flexDirection={'column'}>
              {isFirstWeek && enabled && (
                <>
                  {goals.goals && goals.goals.length > 0 && (
                    <>
                      <p>My upcoming goal:</p>
                      {goals.goals?.map((x) => (
                        <GoalGrid
                          key={x.id}
                          goal={{ goal: x, progress: goals.progress[x.id] }}
                          goalCompletionStatus={goals.progress}
                          handleToggleActivityStatus={() => {}} // No action needed for first week
                          enabled={false} // Disable interaction for first week
                        ></GoalGrid>
                      ))}
                    </>
                  )}
                  <Typography
                    color={'primary'}
                    sx={{
                      marginTop: '20px',
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: '400',
                    }}
                    variant="h5"
                    component="p"
                    gutterBottom
                  >
                    Don&apos;t worry! We won&apos;t start tracking goals until next week.
                  </Typography>
                </>
              )}
              {enabled &&
                !isFirstWeek &&
                goals.goals?.map((x) => (
                  <>
                    <GoalGrid
                      key={x.id}
                      goal={{ goal: x, progress: goals.progress[x.id] }}
                      goalCompletionStatus={goals.progress}
                      handleToggleActivityStatus={confirmGoalDialogController.openConfirmDialog}
                      enabled={enabled}
                    ></GoalGrid>
                  </>
                ))}
            </Grid>
          )}
        </CardContent>
        {enabled && (
          <CardActions
            sx={{
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              flexGrow: 1,
            }}
          >
            <Button variant="outlined" onClick={() => jailCardController.setJailCardOpen(true)}>
              Show Available Jail Cards
            </Button>
            <Button
              disabled={!enabled}
              variant={'contained'}
              onClick={() => exerciseHistoryController.setHistoryDialogOpen(true)}
            >
              View Recent Exercise
            </Button>
            {newGoalEnabled && (
              <Button
                disabled={!newGoalEnabled}
                variant={'contained'}
                onClick={updateGoalsDialogController.handleShowNewGoalDialog}
              >
                Update Goals
              </Button>
            )}
          </CardActions>
        )}
      </Card>

      <ExerciseHistory
        open={exerciseHistoryController.historyDialogOpen}
        onClose={() => exerciseHistoryController.setHistoryDialogOpen(false)}
        view={exerciseHistoryController.activityHistoryView}
      ></ExerciseHistory>

      <JailCardStatusDialog
        jailCards={jailCardController.jailCards}
        onClose={() => jailCardController.setJailCardOpen(false)}
        open={jailCardController.jailCardOpen}
      ></JailCardStatusDialog>

      <ConfirmGoalCompletionDialog
        open={confirmGoalDialogController.confirmGoalDialogOpen}
        onClose={confirmGoalDialogController.cancelConfirmDialog}
        selectedGoal={confirmGoalDialogController.selectedGoal!}
        addActivity={confirmGoalDialogController.addActivity}
        weekCompleted={confirmGoalDialogController.weekCompleted}
      ></ConfirmGoalCompletionDialog>

      {!updateGoalsDialogController.isActivityLookupLoading && updateGoalsDialogController.activityLookupData && (
        <UpdateGoalsDialog
          cohortUserId={cohortUserId}
          showNewGoalDialog={updateGoalsDialogController.showNewGoalDialog && !isDrawerOpen && goals.goals.length === 0}
          submitGoals={async (e, reason, goals) => {
            await Promise.all([
              await updateGoalsDialogController.submitGoals(e, reason, goals),
              await reloadSurveyDrawer(),
              reloadDashboardView?.(),
            ]);
          }}
          groupStartDate={ToDate(groupStartDate)}
          weekNumberIndexZero={weekNumberIndexZero}
          activities={updateGoalsDialogController.activityLookupData.activities}
          timeframes={updateGoalsDialogController.activityLookupData.timeframes}
        ></UpdateGoalsDialog>
      )}
    </>
  );
};

interface GoalGridProps {
  goal: GoalWithStatus;
  enabled: boolean;
  goalCompletionStatus: GoalCompletionStatus;
  handleToggleActivityStatus: (goalDetails: GoalWithStatus, idx: number) => void;
}

const GoalGrid: React.FC<GoalGridProps> = ({ goal, handleToggleActivityStatus, enabled, goalCompletionStatus }) => {
  const uiState = useUiState(enabled);
  const styles = {
    toggleButton: {
      border: 0,
      borderRadius: 30,
      padding: 0.25,
      margin: 0.5,
    } as SxProps,
  };
  return (
    <Grid key={goal.goal.id} sx={{ marginTop: '10px' }}>
      <Typography>{goal.goal.goalText}</Typography>

      {[...Array(goal.goal.frequency)].map((_, idx) =>
        // Only render toggle buttons for goals that have started
        {
          return ToDate(goal.goal.startDate) <= ToDate(DateTime.now()) ? (
            <ToggleButton
              disabled={!enabled}
              color={'primary'}
              onClick={() => {
                const statusArray = goalCompletionStatus[goal.goal.id] ?? [];
                const hasCompleted = statusArray[idx] === GoalStatus.Succeeded;
                if (hasCompleted) return; // prevent action if already completed

                handleToggleActivityStatus(goal, idx);
              }}
              selected={goalCompletionStatus[goal.goal.id]?.includes(idx)}
              key={idx}
              sx={styles.toggleButton}
              value={`${goal.goal.id}-${idx}`}
            >
              {goalCompletionStatus[goal.goal.id]?.[idx] === GoalStatus.Succeeded ? (
                <Circle color={uiState.buttonColor} />
              ) : (
                <CircleOutlined color={uiState.buttonColor} />
              )}
            </ToggleButton>
          ) : null;
        }
      )}
    </Grid>
  );
};
